import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  ApiResourceTable,
  type ApiResourceExtraColumn,
} from "@project/admin/components/api-resource-table";
import { OfflineDonationDialog } from "@project/admin/components/offline-donation-dialog";
import { OfflineDonationDetailDialog } from "@project/admin/components/offline-donation-detail-dialog";
import type { Field } from "@project/admin/components/resource-table";
import { Button } from "@project/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { generateReceiptPdf } from "@project/lib/receipt";
import { offlineDonationService } from "@project/services/admin/offline-donation.service";
import {
  OFFLINE_DONATION_TRUSTS,
  OFFLINE_PAYMENT_MODE_OPTIONS,
  getOfflineDonationPaymentDetails,
  type OfflineDonation,
  type OfflineDonationType,
} from "@project/types/offline-donation";
import {
  ArrowRight,
  Download,
  Eye,
  HandHeart,
  Sprout,
  Stethoscope,
} from "lucide-react";

const trustCards: {
  type: OfflineDonationType;
  icon: typeof HandHeart;
  accent: string;
}[] = [
  { type: "donation", icon: HandHeart, accent: "from-blue-600 to-blue-800" },
  { type: "gaushala", icon: Sprout, accent: "from-amber-400 to-amber-600" },
  { type: "healthcare", icon: Stethoscope, accent: "from-sky-500 to-sky-600" },
];

const typeOptions = (
  Object.keys(OFFLINE_DONATION_TRUSTS) as OfflineDonationType[]
).map(type => ({ value: type, label: OFFLINE_DONATION_TRUSTS[type].title }));

const paymentModeLabel = (mode?: string) =>
  OFFLINE_PAYMENT_MODE_OPTIONS.find(option => option.value === mode)?.label ??
  (mode ? mode.toUpperCase() : "—");

const fields: Field[] = [
  {
    name: "receipt_no",
    label: "Receipt No.",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "donor_name",
    label: "Donor Name",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mobile_number",
    label: "Phone",
    type: "tel",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    currency: true,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "created_at",
    label: "Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
];

const extraColumns: ApiResourceExtraColumn<OfflineDonation>[] = [
  {
    id: "type",
    label: "Trust",
    render: row => (
      <span className="font-semibold text-primary">
        {OFFLINE_DONATION_TRUSTS[row.type]?.title ?? row.type}
      </span>
    ),
  },
  {
    id: "category",
    label: "Category",
    render: row => (
      <div>
        <div>{row.category ?? "—"}</div>
        {row.sub_category && (
          <div className="text-xs text-muted-foreground">
            {row.sub_category}
          </div>
        )}
      </div>
    ),
  },
  {
    id: "payment_mode",
    label: "Payment Mode",
    render: row => paymentModeLabel(row.payment_mode),
  },
];

function downloadReceipt(row: OfflineDonation) {
  const trust = OFFLINE_DONATION_TRUSTS[row.type];
  const particulars = (row.particulars ?? [])
    .map(item => `${item.name}: ₹${item.amount}`)
    .join(", ");
  generateReceiptPdf({
    title: "Donation Receipt",
    receiptNo: row.receipt_no ?? `MR-${row.id}`,
    date: String(row.created_at ?? ""),
    organization: trust?.title,
    donationScheme: [row.category, row.sub_category]
      .filter(Boolean)
      .join(" - "),
    donorName: row.donor_name,
    donorMobile: row.mobile_number,
    donorEmail: row.email ?? "",
    donorAddress: [row.full_address, row.city].filter(Boolean).join(", "),
    donorPan: row.pan_number ?? "",
    amount: parseFloat(row.amount ?? "0"),
    donationType: row.category ?? trust?.defaultCategory,
    paymentMode: paymentModeLabel(row.payment_mode),
    cashOrChequeNo:
      getOfflineDonationPaymentDetails(row).bank_transfer_id ??
      row.payments?.[0]?.razorpay_payment_id ??
      "",
    remarks: particulars,
  });
}

export default function Page() {
  const [activeType, setActiveType] = useState<OfflineDonationType>("donation");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<"all" | OfflineDonationType>(
    "all"
  );
  const [listKey, setListKey] = useState(0);
  const [viewing, setViewing] = useState<OfflineDonation | null>(null);

  const openForm = (type: OfflineDonationType) => {
    setActiveType(type);
    setIsFormOpen(true);
  };

  return (
    <AdminShell
      title="Manual Receipt"
      subtitle="Record offline donations and issue receipts for each trust"
    >
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {trustCards.map(({ type, icon: Icon, accent }) => {
          const trust = OFFLINE_DONATION_TRUSTS[type];
          return (
            <button
              key={type}
              type="button"
              onClick={() => openForm(type)}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <div
                className={`absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`}
              />
              <div className="relative flex items-start gap-3">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-base font-bold text-foreground">
                    {trust.title}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {trust.subtitle}
                  </div>
                </div>
              </div>
              <div className="relative mt-4 flex items-center gap-1 text-sm font-semibold text-primary">
                Create Receipt
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </div>
            </button>
          );
        })}
      </div>

      <ApiResourceTable<OfflineDonation>
        key={`${typeFilter}-${listKey}`}
        title="Manual Receipt"
        labelField="donor_name"
        fields={fields}
        extraColumns={extraColumns}
        searchFields={[
          "receipt_no",
          "donor_name",
          "mobile_number",
          "email",
          "category",
          "sub_category",
        ]}
        defaultValues={{}}
        csvFileName="manual-receipts"
        toolbarActions={
          <Select
            value={typeFilter}
            onValueChange={value =>
              setTypeFilter(value as "all" | OfflineDonationType)
            }
          >
            <SelectTrigger className="h-9 w-full rounded-xl sm:w-60">
              <SelectValue placeholder="All Trusts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Trusts</SelectItem>
              {typeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        list={async query => {
          const result = await offlineDonationService.listPage({
            ...query,
            type: typeFilter === "all" ? undefined : typeFilter,
          });
          return { ...result, data: result.data || [] };
        }}
        renderRowActions={row => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="View Details"
              onClick={() => setViewing(row)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              title="Download Receipt"
              onClick={() => downloadReceipt(row)}
            >
              <Download className="h-4 w-4" />
              <span className="text-xs">Receipt</span>
            </Button>
          </div>
        )}
      />

      <OfflineDonationDetailDialog
        open={viewing !== null}
        onOpenChange={open => {
          if (!open) {
            setViewing(null);
          }
        }}
        donation={viewing}
        onDownloadReceipt={downloadReceipt}
      />

      <OfflineDonationDialog
        open={isFormOpen}
        type={activeType}
        onOpenChange={setIsFormOpen}
        onSuccess={() => setListKey(key => key + 1)}
      />
    </AdminShell>
  );
}
